import { OrdinaryObject, OrdinaryObjectNumber } from "src/modules/utils/shared/ordinary-object.model";
import { TableData } from "./table-data.model";
import { TableColumn } from "../netgen/TableColumn";
import { TableCellType } from "../netgen/TableCellType";
import { Objects } from "src/modules/utils/shared/objects";
import { TableCellEvent } from "./table-cell-event.model";
import { TableCell } from "./table-cell.model";
import { ColumnWidth } from "./column-width.model";

export class TableSizer {

    static perTheme: OrdinaryObject<TableSizer> = {
        balham: new TableSizer("balham"),
        "balham-dark": new TableSizer("balham"),
        quartz: new TableSizer("quartz"),
        default: new TableSizer("balham")
    };

    static get(theme: string): TableSizer {
        let result = TableSizer.perTheme[theme];
        if (result == null) {
            throw new Error("Das Theme " + theme + " wurde nicht gefunden");
        }
        return result;
    }

    public measureFactor = 1.2;
    public themeFactor = 1;

    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;

    private dateTimeWidth: number = null;
    private dateWidth: number = null;
    private durationWidth: number = null;
    private byteSizeWidth: number = null;
    private buttonWidth = 20;
    private padding = 20;
    private numberWidth: OrdinaryObjectNumber<number> = {};
    private textWidth: OrdinaryObjectNumber<number> = {};

    constructor(public theme: string) {
        this.canvas = document.createElement("canvas");
        this.context = this.canvas.getContext("2d");
        
        const probe = document.createElement("div");
        probe.className = "ag-theme-" + this.theme;
        probe.style.display = "none"; // nicht sichtbar machen
        document.body.appendChild(probe);

        const style = getComputedStyle(probe);
        const font = style.font || `${style.fontSize} ${style.fontFamily}`;

        this.context.font = font;

        document.body.removeChild(probe);

        this.padding = this.theme == "balham" ? 8 : 20;
        this.themeFactor = 1; //this.theme == "balham" ? 1 : 1;
    }

    calculate(table: TableData, col: TableColumn, byContent: boolean, useHeaderWidthAsMin = true, forceContentWidth = false): ColumnWidth {
        if (col.flex != null || forceContentWidth) {
            if (byContent) {
                let max = 0;
                let i = 0;
                for (let row of table.rows) {
                    let value = TableCell.getValue(row.values[col.id]);
                    let formattedValue = Objects.toString(col.formatValue(new TableCellEvent(value, row, null, i++, null)));
                    max = Math.max(max, this.measure(this.getTextForMeasurement(formattedValue)) + this.padding);
                }
                max = Math.max(max, this.measure(col.caption) * this.measureFactor + this.padding) * this.themeFactor;
                return new ColumnWidth(null, max);
            }
            else if (col.flex != null) {
                return new ColumnWidth(null, col.flex);
            }
        }
        
        let isFirstColumn = col.id == table.columns[0].id;
        let headerWidth = useHeaderWidthAsMin || col.autoWidth == null ? (this.measure(col.rotate90 ? "XX" : col.caption) * this.measureFactor + this.padding + (isFirstColumn ? 25 : 0)) * this.themeFactor : 0;
        if (col.autoWidth == null) {
            return new ColumnWidth(headerWidth);
        }

        switch (col.cellType) {
            case TableCellType.dateTime:
                if (this.dateTimeWidth == null) {
                    this.dateTimeWidth = this.measure("00.00.0000 00:00:00") + this.padding;
                }
                return new ColumnWidth(Math.max(headerWidth, this.dateTimeWidth), null, null, Math.max(headerWidth, this.dateTimeWidth));
            case TableCellType.date:
                if (this.dateWidth == null) {
                    this.dateWidth = this.measure("00.00.0000") + this.padding;
                }
                return new ColumnWidth(Math.max(headerWidth, this.dateWidth), null, null, Math.max(headerWidth, this.dateWidth));
            case TableCellType.duration:
            case TableCellType.durationNanos:
                if (this.durationWidth == null) {
                    this.durationWidth = this.measure("100s 100ms") + this.padding;
                }
                return new ColumnWidth(Math.max(headerWidth, this.durationWidth), null, null, Math.max(headerWidth, this.durationWidth));
            case TableCellType.byteSize:
                if (this.byteSizeWidth == null) {
                    this.byteSizeWidth = this.measure("1000 Bytes") + this.padding;
                }
                return new ColumnWidth(Math.max(headerWidth, this.byteSizeWidth), null, null, Math.max(headerWidth, this.byteSizeWidth));
            case TableCellType.bool:
            case TableCellType.color:
                return new ColumnWidth(Math.max(headerWidth, 25), null, 25, Math.max(headerWidth, 25));
            case TableCellType.button:
                let w1 = Math.max(headerWidth, this.buttonWidth * (col.autoWidth + 0.5) + this.padding);
                return new ColumnWidth(w1, null, this.buttonWidth * (col.autoWidth + 0.5) + this.padding, w1);
            case TableCellType.int:
                let w2 = this.numberWidth[col.autoWidth];
                if (w2 == null) {
                    w2 = this.measure("0".repeat(Math.min(100, col.autoWidth))) + this.padding;
                    this.numberWidth[col.autoWidth] = w2;
                }
                return new ColumnWidth(Math.max(headerWidth, w2), null, null, Math.max(headerWidth, w2));
            case TableCellType.decimal:
                let w3 = this.numberWidth[col.autoWidth];
                if (w3 == null) {
                    w3 = this.measure("0".repeat(Math.min(100, col.autoWidth))) + this.padding;
                    this.numberWidth[col.autoWidth] = w3;
                }
                return new ColumnWidth(Math.max(headerWidth, w3), null, null, Math.max(headerWidth, w3));
            case TableCellType.string:
            case TableCellType.link:
            default:
                let w4 = this.textWidth[col.autoWidth];
                if (w4 == null) {
                    w4 = this.measure("s".repeat(Math.min(1000, col.autoWidth))) + this.padding;
                    this.textWidth[col.autoWidth] = w4;
                }
                return new ColumnWidth(Math.max(headerWidth, w4));
        }
    }

    private getTextForMeasurement(value: string): string {
        if (!value.includes("<") && !value.includes("&")) {
            return value;
        }
        let element = document.createElement("div");
        element.innerHTML = value;
        return element.textContent ?? "";
    }

    private measure(text: string): number {
        return this.context.measureText(text).width * 1.25;
    }
}